import type { MixPreset } from "../lib/mixPresets";

export interface MemberProfileRequest {
  readonly sessionId: string;
  readonly participantId: string;
}

export interface MemberProfileResponse extends MemberProfileRequest {
  readonly nickname: string;
  readonly mixPresets: MixPreset[];
  readonly profileUpdatedAtEpochMs: number;
}

export interface UpsertMemberProfileRequest extends MemberProfileRequest {
  readonly nickname: string;
  readonly mixPresets: MixPreset[];
}

export interface MemberProfileServiceOptions {
  readonly apiBaseUrl: string;
  readonly authToken?: string | undefined;
  readonly getAuthToken?: (() => Promise<string | undefined>) | undefined;
  readonly onUnauthorized?: (() => Promise<string | undefined>) | undefined;
  readonly fetchImpl?: typeof fetch;
}

const GET_PROFILE_PATH = "/v2/band-sync/livekit/member-profile/get";
const UPSERT_PROFILE_PATH = "/v2/band-sync/livekit/member-profile/upsert";

export class MemberProfileService {
  private readonly apiBaseUrl: string;
  private readonly authToken: string | undefined;
  private readonly getAuthToken: (() => Promise<string | undefined>) | undefined;
  private readonly onUnauthorized: (() => Promise<string | undefined>) | undefined;
  private readonly fetchImpl: typeof fetch;

  constructor(options: MemberProfileServiceOptions) {
    this.apiBaseUrl = options.apiBaseUrl;
    this.authToken = options.authToken;
    this.getAuthToken = options.getAuthToken;
    this.onUnauthorized = options.onUnauthorized;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  private buildEndpoint(path: string): string {
    const trimmedBase = this.apiBaseUrl.trim().replace(/\/$/, "");
    return `${trimmedBase}${path}`;
  }

  private async resolveAuthToken(): Promise<string | undefined> {
    if (this.getAuthToken) {
      return this.getAuthToken();
    }
    return this.authToken;
  }

  private async postWithAuth(
    endpoint: string,
    body: object,
    authToken: string | undefined,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      "content-type": "application/json",
    };
    if (authToken) {
      headers.authorization = `Bearer ${authToken}`;
    }
    return this.fetchImpl(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  }

  private async runWithRetry(endpoint: string, body: object): Promise<Response> {
    const initialToken = await this.resolveAuthToken();
    let response = await this.postWithAuth(endpoint, body, initialToken);
    if (response.status === 401 && this.onUnauthorized) {
      const refreshed = await this.onUnauthorized();
      if (refreshed) {
        response = await this.postWithAuth(endpoint, body, refreshed);
      }
    }
    return response;
  }

  async getProfile(request: MemberProfileRequest): Promise<MemberProfileResponse> {
    const endpoint = this.buildEndpoint(GET_PROFILE_PATH);
    const response = await this.runWithRetry(endpoint, request);
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(`get profile failed (${response.status})${body ? ` body=${body}` : ""}`);
    }
    return (await response.json()) as MemberProfileResponse;
  }

  async upsertProfile(request: UpsertMemberProfileRequest): Promise<MemberProfileResponse> {
    const endpoint = this.buildEndpoint(UPSERT_PROFILE_PATH);
    const response = await this.runWithRetry(endpoint, request);
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(`upsert profile failed (${response.status})${body ? ` body=${body}` : ""}`);
    }
    return (await response.json()) as MemberProfileResponse;
  }
}
